import DataLayerEvent from './DataLayerEvent.js';
import EventAttributeNormalizer from './adapters/EventAttributeNormalizer.js';
import LegacyDataLayerService from './adapters/LegacyDataLayerService.js';
import GA4ToLegacyAdapter from './adapters/GA4ToLegacyAdapter.js';
import EventValidator from './validation/EventValidator.js';
import { isValidationEnabled, getValidatorOptions } from './validation/config.js';
import { isAllowedGA4Event } from './allowedGA4Events.js';
import { applyNettoForGtm, stripNettoFields } from './adapters/nettoValueAdapter.js';
import { getErsMetrikParams } from './utils/ersMetrik.js';

const GA4_DATA_LAYER_NAME = 'ga4DataLayer';
const UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign'];

/**
 * Service-Klasse für das Pushen von Events in die DataLayer.
 *
 * Architektur:
 * - `window.dataLayer` (GTM) erhält jeden Event/Push
 * - `window.ga4DataLayer` erhält zusätzlich GA4-Events (gemäß `isAllowedGA4Event`).
 *   Aus diesem DataLayer liest die `tracking-google-analytics`-Komponente und sendet
 *   Events via gtag → `window.gtagDataLayer` (isoliert).
 * - `window.newDataLayer` (Legacy) erhält Events für Abwärtskompatibilität.
 */
export default class DataLayerService {
    // Statische Variable für einmaliges Logging (über alle Instanzen hinweg)
    static #validationLogShown = false;

    /**
     * Erstellt eine neue DataLayerService-Instanz
     * @param {Object} options - Konfigurationsoptionen
     * @param {string} options.dataLayerName - Name der DataLayer-Variable (Standard: 'dataLayer')
     * @param {boolean} options.enableLegacyPush - Aktiviert parallelen Push in newDataLayer (Standard: true)
     * @param {boolean} options.enableLegacyTransform - Aktiviert automatische Normalisierung von newDataLayer-Events (Standard: true)
     */
    constructor(options = {}) {
        this.dataLayerName = options.dataLayerName || 'dataLayer';
        this.enableLegacyPush = options.enableLegacyPush !== false; // Default: true
        this.enableLegacyTransform = options.enableLegacyTransform !== false; // Default: true

        // Validierung aktivieren:
        // 1. AUTOMATISCH auf Staging (immer)
        // 2. Via localStorage/URL-Parameter
        this.enableValidation = isValidationEnabled();

        // EventValidator initialisieren (lazy, nur wenn benötigt)
        this.validator = null;
        this.validatorOptions = getValidatorOptions();

        // Info-Log wenn Validierung aktiviert wurde (nur einmal global über alle Instanzen)
        if (this.enableValidation && !DataLayerService.#validationLogShown) {
            DataLayerService.#validationLogShown = true;
        }

        // Legacy DataLayer Service initialisieren
        if (this.enableLegacyPush) {
            this.legacyService = new LegacyDataLayerService();
        }

        // Event Attribute Normalizer initialisieren
        if (this.enableLegacyTransform) {
            this.attributeNormalizer = new EventAttributeNormalizer();
        }
    }

    /**
     * Gibt das DataLayer-Array zurück oder initialisiert es
     * @returns {Array} DataLayer-Array
     * @private
     */
    _getDataLayer() {
        if (typeof window === 'undefined') {
            throw new Error('DataLayer is only available in browser environment');
        }

        if (!window[this.dataLayerName]) {
            window[this.dataLayerName] = [];
        }

        return window[this.dataLayerName];
    }

    /**
     * Gibt den GA4-DataLayer zurück oder initialisiert ihn.
     * Aus diesem Layer liest `tracking-google-analytics` und sendet Events via gtag.
     * @returns {Array}
     * @private
     */
    _getGa4DataLayer() {
        if (typeof window === 'undefined') {
            throw new Error('DataLayer is only available in browser environment');
        }

        if (!window[GA4_DATA_LAYER_NAME]) {
            window[GA4_DATA_LAYER_NAME] = [];
        }

        return window[GA4_DATA_LAYER_NAME];
    }

    /**
     * Gibt den EventValidator zurück (lazy initialization)
     * @returns {EventValidator|null}
     * @private
     */
    _getValidator() {
        if (!this.enableValidation) {
            return null;
        }

        if (!this.validator) {
            this.validator = new EventValidator(this.validatorOptions);
        }

        return this.validator;
    }

    /**
     * Validiert ein Event-Objekt
     * @param {Object} eventObject - Das zu validierende Event
     * @private
     */
    _validateEvent(eventObject) {
        const validator = this._getValidator();
        if (!validator) {
            return; // Validierung deaktiviert
        }

        // Nur Events validieren (mit 'event' property)
        if (!eventObject.event) {
            return;
        }

        const result = validator.validate(eventObject);

        // Bei Errors: zusätzlich Warning ausgeben
        if (!result.valid) {
            console.warn('⚠️ [DataLayer] Event wird trotz Validierungsfehlern gepusht');
        }

        // Event wird auch bei Fehlern gepusht (für Kompatibilität mit Legacy-Code)
        // Errors werden nur geloggt
    }


    /**
     * Pusht ein Event-Objekt oder Daten-Update in den DataLayer.
     * GA4-Events werden zusätzlich in den `ga4DataLayer` gepusht (für `tracking-google-analytics`).
     * @param {DataLayerEvent|Object} event - DataLayerEvent-Instanz, Event-Objekt oder Daten-Update
     * @returns {Object} Das gepushte Objekt
     */
    push(event) {
        let eventObject;

        if (event instanceof DataLayerEvent) {
            eventObject = event.toObject();
        } else if (event && typeof event === 'object') {
            eventObject = event;
        } else {
            throw new Error('Event must be a DataLayerEvent instance or an object');
        }

        const isEvent = !!(eventObject.event || eventObject.name);

        // 1. Parallel in Legacy DataLayer pushen (VOR Normalisierung)
        if (this.enableLegacyPush && this.legacyService && isEvent) {
            try {
                const legacyEvent = GA4ToLegacyAdapter.transform(eventObject);
                this.legacyService.push(legacyEvent);
            } catch (error) {
                console.error('[DataLayer] Failed to push to legacy dataLayer:', error);
            }
        }

        // 2. Normalisiere Event-Objekt (für GA4 Standard-Parameter)
        if (this.enableLegacyTransform && this.attributeNormalizer && isEvent) {
            this._normalizeEventObject(eventObject);
        }

        // 3. Event validieren (nur wenn es ein Event mit 'event' property ist)
        this._validateEvent(eventObject);

        // 4. Universelle Anreicherung (UTMs aus URL) — gilt für beide DataLayer
        if (isEvent) {
            this._enrichEvent(eventObject);
        }

        // 5. In den Haupt-DataLayer pushen (GTM lauscht hier; value/price = Netto)
        const dataLayer = this._getDataLayer();
        const gtmEventObject = isEvent ? applyNettoForGtm(eventObject) : eventObject;
        dataLayer.push(gtmEventObject);
        console.debug("dataLayer", gtmEventObject);

        // 6. Wenn GA4-Event: zusätzlich in ga4DataLayer pushen (value/price = Brutto)
        const eventName = eventObject.event || eventObject.name;
        if (isEvent && eventName && isAllowedGA4Event(eventName)) {
            this._getGa4DataLayer().push(stripNettoFields(eventObject));
        }

        return eventObject;
    }

    /**
     * Reichert ein Event-Objekt mit Daten an, die für beide DataLayer (GTM und GA4) gelten:
     * UTM-Parameter aus der URL und die eReader-Metriken aus dem "ers-metrik"-Cookie.
     * @param {Object} eventObject
     * @private
     */
    _enrichEvent(eventObject) {
        this._enrichWithUtmParameters(eventObject);
        this._enrichWithErsMetrik(eventObject);
    }

    /**
     * Ergänzt UTM-Parameter aus der URL, ohne bereits gesetzte Werte zu überschreiben.
     * @param {Object} eventObject
     * @private
     */
    _enrichWithUtmParameters(eventObject) {
        if (typeof window === 'undefined' || !window.location || !window.location.search) {
            return;
        }
        try {
            const searchParams = new URLSearchParams(window.location.search);
            for (const name of UTM_PARAMETERS) {
                if (searchParams.has(name) && eventObject[name] === undefined) {
                    eventObject[name] = searchParams.get(name);
                }
            }
        } catch (error) {
            console.warn('[DataLayer] Failed to read UTM parameters', error);
        }
    }

    /**
     * Ergänzt die eReader-Metriken aus dem "ers-metrik"-Cookie als `ers_*`-Parameter
     * auf Event-Ebene, ohne bereits gesetzte Werte zu überschreiben.
     * @param {Object} eventObject
     * @private
     */
    _enrichWithErsMetrik(eventObject) {
        const ersMetrikParams = getErsMetrikParams();
        for (const [name, value] of Object.entries(ersMetrikParams)) {
            if (eventObject[name] === undefined) {
                eventObject[name] = value;
            }
        }
    }

    /**
     * Normalisiert ein Event-Objekt in-place (modifiziert das Original)
     * Korrigiert fehlerhafte Attribute gemäß GA4 Standards
     * @param {Object} eventObject - Das zu normalisierende Event-Objekt
     * @private
     */
    _normalizeEventObject(eventObject) {
        if (!eventObject || !eventObject.event) {
            return;
        }

        const eventName = eventObject.event;

        // Wenn es ein Ecommerce-Event mit ecommerce-Objekt ist
        if (eventObject.ecommerce && typeof eventObject.ecommerce === 'object') {
            // Normalisiere ecommerce-Parameter mit EventAttributeNormalizer
            const normalizedParams = this.attributeNormalizer.normalizeParams(eventName, eventObject.ecommerce);

            // Überschreibe ecommerce-Objekt mit normalisierten Werten
            eventObject.ecommerce = normalizedParams;
        } else {
            // Non-Ecommerce Event: normalisiere top-level Parameter
            // Extrahiere alle Parameter außer 'event'
            const { event, ...params } = eventObject;
            const normalizedParams = this.attributeNormalizer.normalizeParams(eventName, params);

            // Überschreibe eventObject mit normalisierten Parametern
            Object.keys(params).forEach(key => delete eventObject[key]);
            Object.assign(eventObject, normalizedParams);
            eventObject.event = event; // Event-Name wieder setzen
        }
    }

    /**
     * Erstellt und pusht ein Event direkt mit den übergebenen Parametern
     * @param {string} eventName - Name des Events
     * @param {Object} params - Event-Parameter (ohne items)
     * @param {Array<Object>} items - Optional: Items-Array
     * @returns {Object} Das gepushte Event-Objekt
     */
    pushEvent(eventName, params = {}, items = null) {
        const event = new DataLayerEvent(eventName, params, items);
        return this.push(event);
    }

    /**
     * Pusht ein Datenobjekt direkt in den DataLayer (ohne Event-Name)
     * Wird verwendet für Basedata, Konfiguration etc.
     * @param {Object} data - Datenobjekt mit Key-Value-Paaren
     * @returns {Object} Das gepushte Datenobjekt
     */
    pushData(data) {
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('Data must be an object');
        }

        const dataLayer = this._getDataLayer();
        dataLayer.push(data);

        // Debug-Log für GA4 DataLayer
        console.debug("dataLayer", data);

        // Parallel in Legacy DataLayer pushen
        if (this.enableLegacyPush && this.legacyService) {
            try {
                this.legacyService.push(data);
            } catch (error) {
                console.error('[DataLayer] Failed to push data to legacy dataLayer:', error);
            }
        }

        return data;
    }

    /**
     * Gibt die Anzahl der Events im DataLayer zurück
     * @returns {number} Anzahl der Events
     */
    getEventCount() {
        return this._getDataLayer().length;
    }

    /**
     * Gibt alle Events aus dem DataLayer zurück
     * @returns {Array} Array aller DataLayer-Einträge
     */
    getAllEvents() {
        return [...this._getDataLayer()];
    }
}
